import { forwardRef, useEffect, useId, useRef, useState, type InputHTMLAttributes } from "react";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | null;
  hint?: string;
}

/** Labelled input with hint and inline error wired through aria-describedby. */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ label, error, hint, id, ...input }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = [hint ? `${inputId}-hint` : null, error ? `${inputId}-err` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <input ref={ref} id={inputId} aria-invalid={Boolean(error)} aria-describedby={describedBy} {...input} />
      {hint && (
        <span id={`${inputId}-hint`} className="field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={`${inputId}-err`} className="field-error">
          {error}
        </span>
      )}
    </div>
  );
});

/** Password input with a show/hide toggle; paste and password managers stay allowed. */
export function PasswordField(props: Omit<FieldProps, "type">) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const autoId = useId();
  const id = props.id ?? autoId;
  const describedBy = [props.hint ? `${id}-hint` : null, props.error ? `${id}-err` : null].filter(Boolean).join(" ") || undefined;
  const { label, error, hint, ...input } = props;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="password-wrap">
        <input {...input} id={id} type={visible ? "text" : "password"} aria-invalid={Boolean(error)} aria-describedby={describedBy} />
        <button type="button" className="password-toggle" aria-controls={id} aria-pressed={visible} onClick={() => setVisible((v) => !v)}>
          {visible ? t("auth.hidePassword") : t("auth.showPassword")}
        </button>
      </div>
      {hint && (
        <span id={`${id}-hint`} className="field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={`${id}-err`} className="field-error">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Error summary shown after a failed submit. Receives focus so keyboard and
 * screen-reader users land on it; each item links to its field.
 */
export function ErrorSummary({ errors, fieldIds, focusKey }: { errors: Record<string, string>; fieldIds: Record<string, string>; focusKey: number }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  // A field fixed on blur keeps its key with "": nothing to show for it.
  const entries = Object.entries(errors).filter(([, key]) => key);
  useEffect(() => {
    if (entries.length) ref.current?.focus();
    // Refocus on each failed submit, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);
  if (!entries.length) return null;
  return (
    <div className="error-summary" role="alert" tabIndex={-1} ref={ref} aria-labelledby="error-summary-title">
      <h2 id="error-summary-title">{t("auth.errorSummary")}</h2>
      <ul>
        {entries.map(([field, key]) => (
          <li key={field}>{fieldIds[field] ? <a href={`#${fieldIds[field]}`}>{t(key as MessageKey)}</a> : t(key as MessageKey)}</li>
        ))}
      </ul>
    </div>
  );
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
